<template>
  <v-dialog v-model="open" max-width="700">
    <v-card>
      <v-card-title>Erinnerung versenden</v-card-title>
      <v-card-text>
        <!-- Month selector -->
        <v-btn-toggle
          v-model="selectedMonthIndex"
          mandatory
          density="compact"
          variant="outlined"
          color="primary"
          class="mb-4"
        >
          <v-btn v-for="(opt, i) in monthOptions" :key="i" :value="i">
            {{ opt.label }}
          </v-btn>
        </v-btn-toggle>

        <div v-if="previewLoading" class="text-center py-4">
          <v-progress-circular indeterminate />
        </div>
        <div v-else-if="preview">
          <!-- Recipients -->
          <p class="text-subtitle-2 mb-2">
            Empfänger ({{ selectedEmployeeIds.length }} / {{ preview.employees.length }}):
          </p>
          <v-chip-group
            v-model="selectedEmployeeIds"
            multiple
            selected-class="text-primary"
            column
          >
            <v-chip
              v-for="emp in preview.employees"
              :key="emp.id"
              :value="emp.id"
              size="small"
              :variant="isSubmitted(emp) ? 'flat' : 'outlined'"
              :color="isSubmitted(emp) ? 'success' : undefined"
              filter
            >
              {{ emp.first_name }} {{ emp.last_name }}
              <v-icon v-if="isSubmitted(emp)" size="x-small" class="ml-1">mdi-check-circle</v-icon>
              <span class="text-grey ml-1">({{ emp.email }})</span>
            </v-chip>
          </v-chip-group>

          <!-- Email Text -->
          <v-textarea
            v-model="reminderText"
            label="E-Mail-Text"
            hint="Platzhalter: {name} = vollständiger Name (Vor- und Nachname), {vorname} = nur Vorname. Der Link zum Stundenzettel wird automatisch angehängt."
            persistent-hint
            rows="6"
            class="mt-4"
            variant="outlined"
          />
        </div>
        <div v-else-if="noMissing" class="text-center py-4 text-medium-emphasis">
          Alle Mitarbeiter haben ihren Stundenzettel bereits eingereicht.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="open = false">Abbrechen</v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="sending"
          :disabled="!preview || selectedEmployeeIds.length === 0"
          @click="send"
        >
          {{ selectedEmployeeIds.length }} Erinnerung{{ selectedEmployeeIds.length !== 1 ? 'en' : '' }} senden
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { api } from '@/services/api'
import { useNotificationStore } from '@/stores/notification'
import type { TimesheetReminderPreview } from '@/types'

const open = defineModel<boolean>({ default: false })

const emit = defineEmits<{
  sent: []
}>()

const notificationStore = useNotificationStore()

const preview = ref<TimesheetReminderPreview | null>(null)
const previewLoading = ref(false)
const sending = ref(false)
const reminderText = ref('')
const selectedEmployeeIds = ref<number[]>([])
const noMissing = ref(false)
const selectedMonthIndex = ref(0)

const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

const monthOptions = computed(() => {
  const now = new Date()
  const curr = { year: now.getFullYear(), month: now.getMonth() + 1 }
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const last = { year: prev.getFullYear(), month: prev.getMonth() + 1 }
  return [
    { label: `${monthNames[last.month - 1]} ${last.year}`, ...last },
    { label: `${monthNames[curr.month - 1]} ${curr.year}`, ...curr },
  ]
})

const targetMonth = computed(() => monthOptions.value[selectedMonthIndex.value])

watch(open, async (isOpen) => {
  if (isOpen) {
    await loadPreview()
  } else {
    preview.value = null
    reminderText.value = ''
    selectedEmployeeIds.value = []
    noMissing.value = false
  }
})

watch(selectedMonthIndex, () => {
  if (open.value) {
    loadPreview()
  }
})

async function loadPreview() {
  previewLoading.value = true
  noMissing.value = false
  preview.value = null
  try {
    preview.value = await api.previewTimesheetReminders(
      targetMonth.value.year, targetMonth.value.month,
    )
    reminderText.value = preview.value.default_text
    const submittedStatuses = ['PENDING_APPROVAL', 'APPROVED', 'RELEASED']
    selectedEmployeeIds.value = preview.value.employees
      .filter(e => !submittedStatuses.includes(e.timesheet_status ?? ''))
      .map(e => e.id)
    if (preview.value.employees.length === 0) {
      noMissing.value = true
    }
  } finally {
    previewLoading.value = false
  }
}

function isSubmitted(emp: { timesheet_status?: string | null }) {
  return ['PENDING_APPROVAL', 'APPROVED', 'RELEASED'].includes(emp.timesheet_status ?? '')
}

async function send() {
  sending.value = true
  try {
    const result = await api.sendTimesheetReminders(
      targetMonth.value.year, targetMonth.value.month,
      reminderText.value, selectedEmployeeIds.value,
    )
    open.value = false
    notificationStore.success(
      `${result.sent_count} Erinnerung(en) versendet${result.failed_count > 0 ? `, ${result.failed_count} fehlgeschlagen` : ''}`,
    )
    emit('sent')
  } catch {
    notificationStore.error('Fehler beim Versenden der Erinnerungen')
  } finally {
    sending.value = false
  }
}
</script>
